import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import BreadcrumbNav from '@/components/feature/BreadcrumbNav';
import { faqs, aboutStats, milestones, contactInfo, cooperationTypes } from '@/mocks/about';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '韩漫在线',
  url: 'http://wxctif.com/',
  description: '专业韩国漫画在线阅读平台，提供免费下拉式韩国漫画全集。高清画质和流畅阅读体验，致力于成为中文世界最优质的韩漫在线阅读平台。',
  address: {
    '@type': 'PostalAddress',
    streetAddress: '望京SOHO T1座1206室',
    addressLocality: '北京',
    addressRegion: '北京市',
    addressCountry: 'CN',
  },
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+86-400-888-6666',
    email: 'hello@wxctif.com',
    contactType: 'customer service',
  },
  sameAs: ['http://wxctif.com/'],
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
};

const bcSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: '首页', item: 'http://wxctif.com/' },
    { '@type': 'ListItem', position: 2, name: '关于我们' },
  ],
};

export default function AboutPage() {
  return (
    <>
      <SEOHead
        title="关于我们"
        description="韩漫在线是专业的韩国漫画在线阅读平台。了解我们的品牌故事、发展历程、核心数据以及商务合作联系方式。"
        keywords="韩漫在线,关于韩漫在线,韩漫品牌,韩漫平台介绍"
        canonicalUrl="http://wxctif.com/about"
        schema={[aboutSchema, faqSchema, bcSchema]}
      />
      <Navbar />
      <main className="pt-16 md:pt-20">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <BreadcrumbNav
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>
        </div>

        <section className="py-10 md:py-16">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto text-center">
              <p className="text-primary-500 text-xs md:text-sm font-medium tracking-wider uppercase mb-3">
                关于韩漫在线
              </p>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-5">
                为每一位韩漫爱好者<br />打造极致的阅读体验
              </h1>
              <p className="text-foreground-500 text-sm md:text-base leading-relaxed max-w-2xl mx-auto">
                韩漫在线成立于2018年，是中文世界领先的韩国漫画在线阅读平台。
                我们与韩国多家顶级漫画平台达成深度合作，为中文读者带来最优质、最及时的韩漫内容。
              </p>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 text-center mb-10">
                平台数据
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                {aboutStats.map((stat) => (
                  <div key={stat.label} className="bg-background-50 rounded-lg p-5 md:p-6 text-center">
                    <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-primary-100 flex items-center justify-center">
                      <i className={`${stat.icon} text-primary-500 text-lg`}></i>
                    </div>
                    <p className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-1">{stat.value}</p>
                    <p className="text-foreground-500 text-xs">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 text-center mb-10">
                发展历程
              </h2>
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-200 -translate-x-1/2"></div>
                <div className="flex flex-col gap-8">
                  {milestones.map((m, i) => (
                    <div
                      key={m.year}
                      className={`relative flex flex-col md:flex-row gap-4 ${
                        i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                      }`}
                    >
                      <div className="absolute left-4 md:left-1/2 w-3 h-3 rounded-full bg-primary-500 -translate-x-1/2 mt-1.5 z-10 ring-4 ring-background-50"></div>
                      <div className={`md:w-1/2 ${i % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'} pl-10`}>
                        <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold mb-2">
                          {m.year}
                        </span>
                        <h3 className="font-heading text-base font-bold text-foreground-900 mb-1">{m.title}</h3>
                        <p className="text-foreground-500 text-sm leading-relaxed">{m.description}</p>
                      </div>
                      <div className="md:w-1/2"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 text-center mb-10">
                商务合作
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-12">
                {cooperationTypes.map((coop) => (
                  <div key={coop.title} className="bg-background-50 rounded-lg p-5 md:p-6">
                    <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center mb-4">
                      <i className={`${coop.icon} text-accent-600 text-lg`}></i>
                    </div>
                    <h3 className="font-heading text-base font-bold text-foreground-900 mb-2">{coop.title}</h3>
                    <p className="text-foreground-500 text-sm leading-relaxed">{coop.description}</p>
                  </div>
                ))}
              </div>

              <div className="max-w-lg mx-auto bg-background-50 rounded-lg p-6 md:p-8">
                <h3 className="font-heading text-lg font-bold text-foreground-900 mb-4 text-center">联系我们</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-primary-500"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">公司地址</p>
                      <p className="text-sm text-foreground-700">{contactInfo.address}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-primary-500"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">联系电话</p>
                      <p className="text-sm text-foreground-700">{contactInfo.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-primary-500"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">电子邮箱</p>
                      <p className="text-sm text-foreground-700">{contactInfo.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-wechat-line text-primary-500"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">微信客服</p>
                      <p className="text-sm text-foreground-700">{contactInfo.wechat}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-primary-500"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">工作时间</p>
                      <p className="text-sm text-foreground-700">{contactInfo.workHours}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 text-center mb-10">
                常见问题
              </h2>
              <div className="space-y-3">
                {faqs.map((faq, i) => (
                  <details key={i} className="group border border-background-200 rounded-lg overflow-hidden">
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-50 transition-colors list-none">
                      <span className="font-heading text-sm md:text-base font-semibold text-foreground-900 pr-4">{faq.question}</span>
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                    </summary>
                    <div className="px-5 pb-4">
                      <p className="text-foreground-600 text-sm leading-relaxed">{faq.answer}</p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}